export type Accent =
  | "lavender"
  | "peach"
  | "sage"
  | "sky"
  | "butter"
  | "blush"
  | "mint";

export const ACCENTS: Accent[] = [
  "lavender",
  "peach",
  "sage",
  "sky",
  "butter",
  "blush",
  "mint",
];

export type Thread = {
  id: string;
  name: string;
  kind: "lanes" | "flat";
  accent: Accent;
  sort_order: number;
  archived: number;
  pinned: number;
  created_at: number;
  updated_at: number;
};

export type Lane = {
  id: string;
  thread_id: string;
  name: string;
  accent: Accent;
  sort_order: number;
  created_at: number;
};

export type Card = {
  id: string;
  thread_id: string;
  lane_id: string | null;
  title: string;
  body: string;
  accent: Accent | null;
  sort_order: number;
  created_at: number;
  updated_at: number;
  deleted_at?: number | null;
};

// A card in the Bin includes joined thread/lane names so the UI can show
// "from <thread> / <lane>" context without fetching them separately.
export type BinCard = Card & {
  deleted_at: number;
  thread_name: string;
  lane_name: string | null;
};

// Tailwind-safe accent → class mappings. Stating each combination explicitly
// is required so Tailwind's content scanner sees the class names; building
// them dynamically (e.g. `bg-${accent}-100`) would break in production.
export const accentClasses: Record<
  Accent,
  {
    chipBg: string;
    chipText: string;
    laneBg: string;
    laneBorder: string;
    dot: string;
    cardLeft: string;
    sidebarActive: string;
    softBg: string;
  }
> = {
  lavender: {
    chipBg: "bg-lavender-100",
    chipText: "text-lavender-400",
    laneBg: "bg-lavender-50/60",
    laneBorder: "border-lavender-100",
    dot: "bg-lavender-200",
    cardLeft: "before:bg-lavender-200",
    sidebarActive: "bg-lavender-50 text-ink",
    softBg: "bg-lavender-50",
  },
  peach: {
    chipBg: "bg-peach-100",
    chipText: "text-peach-400",
    laneBg: "bg-peach-50/60",
    laneBorder: "border-peach-100",
    dot: "bg-peach-200",
    cardLeft: "before:bg-peach-200",
    sidebarActive: "bg-peach-50 text-ink",
    softBg: "bg-peach-50",
  },
  sage: {
    chipBg: "bg-sage-100",
    chipText: "text-sage-400",
    laneBg: "bg-sage-50/60",
    laneBorder: "border-sage-100",
    dot: "bg-sage-200",
    cardLeft: "before:bg-sage-200",
    sidebarActive: "bg-sage-50 text-ink",
    softBg: "bg-sage-50",
  },
  sky: {
    chipBg: "bg-sky-100",
    chipText: "text-sky-400",
    laneBg: "bg-sky-50/60",
    laneBorder: "border-sky-100",
    dot: "bg-sky-200",
    cardLeft: "before:bg-sky-200",
    sidebarActive: "bg-sky-50 text-ink",
    softBg: "bg-sky-50",
  },
  butter: {
    chipBg: "bg-butter-100",
    chipText: "text-butter-400",
    laneBg: "bg-butter-50/60",
    laneBorder: "border-butter-100",
    dot: "bg-butter-200",
    cardLeft: "before:bg-butter-200",
    sidebarActive: "bg-butter-50 text-ink",
    softBg: "bg-butter-50",
  },
  blush: {
    chipBg: "bg-blush-100",
    chipText: "text-blush-400",
    laneBg: "bg-blush-50/60",
    laneBorder: "border-blush-100",
    dot: "bg-blush-200",
    cardLeft: "before:bg-blush-200",
    sidebarActive: "bg-blush-50 text-ink",
    softBg: "bg-blush-50",
  },
  mint: {
    chipBg: "bg-mint-100",
    chipText: "text-mint-400",
    laneBg: "bg-mint-50/60",
    laneBorder: "border-mint-100",
    dot: "bg-mint-200",
    cardLeft: "before:bg-mint-200",
    sidebarActive: "bg-mint-50 text-ink",
    softBg: "bg-mint-50",
  },
};

// Used by the card accent strip; stronger than the lane accents above so the
// strip remains visible against pastel lane backgrounds.
export const accentStrong: Record<Accent, string> = {
  lavender: "before:bg-lavender-400",
  peach: "before:bg-peach-400",
  sage: "before:bg-sage-400",
  sky: "before:bg-sky-400",
  butter: "before:bg-butter-400",
  blush: "before:bg-blush-400",
  mint: "before:bg-mint-400",
};
